import { useState, useRef } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, Stack
} from '@mui/material';
import { Add, Edit, Delete, Download, Upload } from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { downloadBlob, readFileAsText } from './csvUtils';

const SERVICE_TYPES = ['therapy', 'salon', 'spa'];
const EMPTY = { name: '', service_type: 'therapy', price: '', duration: '', description: '' };

export default function TreatmentsTab({ rows, onRefresh, showSnackbar }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const uploadRef = useRef();

  const openAdd = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (row) => {
    setForm({
      name: row.name,
      service_type: row.service_type || 'therapy',
      price: row.price,
      duration: row.duration || '',
      description: row.description || ''
    });
    setEditId(row.id);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) { showSnackbar('Name and price are required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editId) {
        await adminAPI.updateTreatment(editId, payload);
        showSnackbar('Treatment updated');
      } else {
        await adminAPI.createTreatment(payload);
        showSnackbar('Treatment created');
      }
      setOpen(false);
      onRefresh();
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await adminAPI.deleteTreatment(deleteId);
      showSnackbar('Treatment deleted');
      setDeleteId(null);
      onRefresh();
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Delete failed', 'error');
      setDeleteId(null);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const res = await adminAPI.downloadTreatmentsCSV();
      downloadBlob(res.data, 'treatments.csv');
    } catch { showSnackbar('CSV download failed', 'error'); }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const csv = await readFileAsText(file);
      const res = await adminAPI.uploadTreatmentsCSV(csv);
      const { inserted, updated, errors } = res.data.data;
      const msg = `Inserted: ${inserted}, Updated: ${updated}${errors?.length ? `, Errors: ${errors.length}` : ''}`;
      showSnackbar(msg, errors?.length ? 'warning' : 'success');
      onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'CSV upload failed', 'error'); }
  };

  const serviceColor = (t) => t === 'salon' ? 'secondary' : t === 'spa' ? 'primary' : 'default';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Treatments ({rows.length})</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Download />} onClick={handleDownloadCSV}>
            Download CSV
          </Button>
          <Button size="small" variant="outlined" startIcon={<Upload />} onClick={() => uploadRef.current?.click()}>
            Upload CSV
          </Button>
          <input ref={uploadRef} type="file" accept=".csv" hidden onChange={handleUploadCSV} />
          <Button size="small" variant="contained" startIcon={<Add />} onClick={openAdd}>
            Add Treatment
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell align="right"><strong>Price (₹)</strong></TableCell>
              <TableCell><strong>Duration</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No treatments found
                </TableCell>
              </TableRow>
            )}
            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  <Chip label={row.service_type} size="small" color={serviceColor(row.service_type)} />
                </TableCell>
                <TableCell align="right">{parseFloat(row.price).toLocaleString('en-IN')}</TableCell>
                <TableCell>{row.duration ? `${row.duration} min` : '—'}</TableCell>
                <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={row.description || ''}><span>{row.description || '—'}</span></Tooltip>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Treatment' : 'Add Treatment'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Service Type</InputLabel>
            <Select value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))} label="Service Type">
              {SERVICE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Price (₹) *" type="number" value={form.price}
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))} fullWidth />
            <TextField label="Duration (min)" type="number" value={form.duration}
              onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} fullWidth />
          </Box>
          <TextField label="Description" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Treatment?</DialogTitle>
        <DialogContent>If the treatment is referenced in any bills or plans, deletion will be rejected.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
